import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService } from '@assetaccountant/core';
import { ValidSubmitEvent } from '@assetaccountant/core/directives/forms/valid-submit.directive';
import {
  HasChanges,
  hasChangesOnBeforeUnloadListener,
} from '@assetaccountant/core/guards/has-changes';
import { Register } from '@assetaccountant/core/models/register';
import { AppNavigationService } from '@assetaccountant/core/services/app-navigation.service';
import { OrganisationService } from '@assetaccountant/core/services/organisation.service';
import { TypedFormControl, TypedFormGroup } from '@assetaccountant/core/utils/typed-forms';
import { StripeLoader } from '@assetaccountant/organisation-settings/services/stripe.loader';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  Stripe,
  StripeCardCvcElement,
  StripeCardCvcElementOptions,
  StripeCardExpiryElement,
  StripeCardExpiryElementOptions,
  StripeCardNumberElement,
  StripeCardNumberElementOptions,
} from '@stripe/stripe-js';

import { CountryDto } from '@assetaccountant/api/models/country.dto';
import { OrganisationPermissionType } from '@assetaccountant/api/models/organisation-permission-type';
import { OrganisationDto } from '@assetaccountant/api/models/organisation.dto';
import { RegisterApi } from '@assetaccountant/api/services/register.api';
import { AuthorizationService } from '@assetaccountant/core/services/authorization.service';
import { Subscription } from 'rxjs';
import {
  PaymentMethod,
  PaymentService,
} from '../../../organisation-settings/services/payment.service';

interface AaStripeElement {
  on(eventType: 'ready', handler: () => void): this;
  on(eventType: 'change', handler: (ev: { error?: { message: string } }) => void): this;
  mount(domElement: string | HTMLElement): void;
}

async function prepareStripeElement<T extends AaStripeElement>(
  element: T,
  onChange: (error?: string) => void,
  nativeElement: HTMLElement,
) {
  await new Promise(resolve => {
    element
      .on('ready', () => resolve(null))
      .on('change', ({ error }) => onChange(error && error.message))
      .mount(nativeElement);
  });
}

@Component({
  selector: 'app-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
  standalone: false,
})
export class PaymentComponent implements OnInit, OnDestroy, AfterViewInit, HasChanges {
  @Input() activeOrganisation: OrganisationDto;
  @Input() form: TypedFormGroup<{
    paymentMethod: TypedFormControl<string>;
    countryId: TypedFormControl<number>;
  }>;
  @Input() parentComponent: 'page' | 'modal';

  @Output() failedToAttach = new EventEmitter<void>();
  @Output() successfullyAttached = new EventEmitter<void>();

  @ViewChild('cardNumber') cardNumberEl: ElementRef<HTMLElement>;
  @ViewChild('cardExpiry') cardExpiryEl: ElementRef<HTMLElement>;
  @ViewChild('cardCvc') cardCvcEl: ElementRef<HTMLElement>;

  stripe: Stripe | null = null;

  cardNumber: StripeCardNumberElement;
  cardExpiry: StripeCardExpiryElement;
  cardCvc: StripeCardCvcElement;

  cardNumberError?: string;
  cardExpiryError?: string;
  cardCvcError?: string;

  canUpdatesubscription = false;
  countries: CountryDto[] = [];
  currentPlanId: string;
  currentQty = 1;
  errorMessages: string[] = [];
  register: Register;
  subscriptions: Subscription[] = [];
  unknownError = false;

  paymentMethods?: PaymentMethod[];

  private stripeStatus: 'LOADING' | 'SUCCESS' | 'FAILURE' = 'LOADING';
  private methodsStatus: 'LOADING' | 'SUCCESS' | 'FAILURE' = 'LOADING';
  get loadStatus(): 'LOADING' | 'SUCCESS' | 'FAILURE' {
    if (this.stripeStatus === 'FAILURE' || this.methodsStatus === 'FAILURE') {
      return 'FAILURE';
    }
    if (this.stripeStatus === 'LOADING' || this.methodsStatus === 'LOADING') {
      return 'LOADING';
    }
    return 'SUCCESS';
  }

  constructor(
    public ngbModal: NgbModal,
    private paymentService: PaymentService,
    private appNavigationService: AppNavigationService,
    private organisationService: OrganisationService,
    private router: Router,
    private authService: AuthenticationService,
    private stripeLoader: StripeLoader,
    private registerApi: RegisterApi,
    private authorizationService: AuthorizationService,
  ) {}

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload = hasChangesOnBeforeUnloadListener;

  get hasChanges() {
    return this.form.dirty;
  }

  ngOnInit() {
    this.updatePaymentMethods();

    this.subscriptions = [
      this.registerApi
        .getCountries({ includeCountryId: this.activeOrganisation?.billingCountryId || undefined })
        .subscribe(result => {
          this.countries = result;
        }),

      this.authorizationService
        .isOrganisationAuthorized(
          this.activeOrganisation.organisationId,
          OrganisationPermissionType.UpdateSubscription,
        )
        .subscribe(canUpdatesubscription => {
          this.canUpdatesubscription = canUpdatesubscription;

          if (canUpdatesubscription) {
            this.form.controls.countryId.enable();
          } else {
            this.form.controls.countryId.disable();
          }
        }),
    ];

    if (this.activeOrganisation?.billingCountryId) {
      this.form.patchValue({ countryId: this.activeOrganisation.billingCountryId });
    } else {
      this.organisationService.listRegisters().subscribe(result => {
        if (result.registers?.length) {
          this.form.patchValue({ countryId: result.registers[0].countryId });
        }
      });
    }
  }

  ngOnDestroy() {
    if (this.cardNumber) {
      this.cardNumber.unmount();
    }
    if (this.cardExpiry) {
      this.cardExpiry.unmount();
    }
    if (this.cardCvc) {
      this.cardCvc.unmount();
    }

    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private updatePaymentMethods() {
    this.methodsStatus = 'LOADING';
    this.paymentService.getPaymentMethods().subscribe({
      next: next => {
        this.paymentMethods = next;
        // Put primary payment method first
        this.paymentMethods.sort((a, b) => (a.isPrimary ? -1 : b.isPrimary ? 1 : 0));

        const activeMethod = this.paymentMethods.find(pm => pm.isPrimary);
        if (activeMethod) {
          this.form.controls.paymentMethod.setValue(activeMethod.id);
        }

        this.form.markAsPristine();
        this.form.markAsUntouched();

        this.methodsStatus = 'SUCCESS';
      },
      error: () => {
        this.methodsStatus = 'FAILURE';
      },
    });
  }

  ngAfterViewInit() {
    this.loadStripe();
  }

  async loadStripe() {
    this.stripe = await this.stripeLoader.load().catch(() => null);

    if (this.stripe) {
      const elements = this.stripe.elements();

      const options:
        | StripeCardNumberElementOptions
        | StripeCardExpiryElementOptions
        | StripeCardCvcElementOptions = {
        classes: {
          base: 'form-control StripeElement',
          invalid: 'is-invalid',
        },
      };

      this.cardNumber = elements.create('cardNumber', { ...options, showIcon: true });
      this.cardExpiry = elements.create('cardExpiry', options);
      this.cardCvc = elements.create('cardCvc', options);

      await Promise.all([
        prepareStripeElement(
          this.cardNumber,
          err => (this.cardNumberError = err),
          this.cardNumberEl.nativeElement,
        ),
        prepareStripeElement(
          this.cardExpiry,
          err => (this.cardExpiryError = err),
          this.cardExpiryEl.nativeElement,
        ),
        prepareStripeElement(
          this.cardCvc,
          err => (this.cardCvcError = err),
          this.cardCvcEl.nativeElement,
        ),
      ]);

      this.stripeStatus = 'SUCCESS';
    } else {
      this.stripeStatus = 'FAILURE';
    }
  }

  async onSubmit(_$event: ValidSubmitEvent) {
    this.errorMessages = [];
    this.unknownError = false;

    const paymentMethodId = this.form.value.paymentMethod;

    if (paymentMethodId) {
      if (this.canUpdatesubscription) {
        this.organisationService.setBillingCountry(this.form.value.countryId).subscribe();
      }

      this.paymentService
        .setPrimaryPaymentMethod({
          paymentMethodId,
        })
        .subscribe(() => this.onSuccess());
      return;
    }

    const userEmail = this.authService.getCurrentUserEmail();

    if (this.stripe) {
      try {
        const result = await this.stripe.createPaymentMethod({
          type: 'card',
          card: this.cardNumber,
          billing_details: {
            email: userEmail,
          },
        });

        if (result.error) {
          if (result.error.message) {
            this.errorMessages.push(result.error.message);
          } else {
            this.unknownError = true;
            console.error(
              'Did not receive a proper error from stripe when creating payment method',
              result,
            );
          }
          this.failedToAttach.emit();
        } else if (result.paymentMethod) {
          if (this.canUpdatesubscription) {
            this.organisationService.setBillingCountry(this.form.value.countryId).subscribe();
          }
          this.paymentService
            .attachPaymentMethod({ paymentMethodId: result.paymentMethod.id })
            .subscribe({
              next: () => this.onSuccess(),
              error: err => {
                try {
                  const errorMessages = Object.values(JSON.parse(err.error));
                  Array.prototype.push.apply(this.errorMessages, errorMessages);
                } catch {
                  throw err;
                }
                this.failedToAttach.emit();
              },
            });
        } else {
          this.unknownError = true;
          console.error(
            'Did not receive a payment method or error from stripe when creating payment method',
            result,
          );
        }
      } catch (err) {
        this.errorMessages.push(err.message);
        this.failedToAttach.emit();
      }
    }
  }

  private onSuccess() {
    this.successfullyAttached.emit();
    this.updatePaymentMethods();
  }

  exit() {
    this.router.navigate(['organisations', this.appNavigationService.getActiveRegisterId() || '-']);
  }
}
